import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LandingNav from "@/components/LandingNav";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft } from "lucide-react";
import { blogPosts, getPostBySlug } from "@/content/blogPosts";

const BlogPost = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SEOHead
          title={t("blog.notFoundTitle", "Post not found | TestAssets Blog")}
          description={t("blog.notFoundDescription", "This blog post could not be found.")}
          path={`/blog/${slug ?? ""}`}
          noindex
        />
        <LandingNav />
        <main className="flex-1 container mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4">{t("blog.notFoundHeading", "Post not found")}</h1>
          <Button asChild variant="primary">
            <Link to="/blog">{t("blog.backToBlog", "Back to blog")}</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "TestAssets" },
    publisher: {
      "@type": "Organization",
      name: "TestAssets",
      logo: { "@type": "ImageObject", url: "https://testassets.io/icon.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://testassets.io/blog/${post.slug}`,
    },
    keywords: post.tags.join(", "),
  };
  const jsonLd =
    post.faqs && post.faqs.length > 0
      ? [
          articleJsonLd,
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : articleJsonLd;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={post.metaTitle}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={jsonLd}
        ogType="article"
        breadcrumbs={[
          { name: t("nav.start"), url: "/" },
          { name: t("nav.blog", "Blog"), url: "/blog" },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
      />
      <LandingNav />
      <main className="flex-1 container mx-auto px-6 py-12 max-w-3xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/blog">
            <ArrowLeft />
            {t("blog.backToBlog", "Back to blog")}
          </Link>
        </Button>

        <article>
          <header className="mb-10">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
              {post.title}
            </h1>
            <p className="text-lg text-muted-foreground mb-4">{post.description}</p>
            <p className="text-sm text-muted-foreground">
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
              {" · "}
              {t("blog.readMinutes", "{{count}} min read", { count: post.readMinutes })}
            </p>
          </header>

          <div className="prose prose-invert max-w-none">
            <p className="text-base md:text-lg leading-relaxed text-foreground/90 mb-8">
              {post.intro}
            </p>
            {post.sections.map((section) => (
              <section key={section.heading} className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mt-8 mb-3">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-foreground/85 mb-4">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </article>

        {post.faqs && post.faqs.length > 0 && (
          <section className="mt-12 pt-8 border-t border-border">
            <h2 className="text-2xl font-semibold text-foreground mb-6">
              {t("blog.faqHeading", "Frequently asked questions")}
            </h2>
            <Accordion type="single" collapsible>
              {post.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {related.length > 0 && (
          <aside className="mt-16 pt-10 border-t border-border">
            <h2 className="text-xl font-semibold text-foreground mb-6">
              {t("blog.relatedHeading", "Keep reading")}
            </h2>
            <ul className="space-y-4">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="block group"
                  >
                    <h3 className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;